/**
 * Patient Profile overhaul — "start a follow-up" from the profile's Follow-up button.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * A port of the classic page's "Initiate a Follow-up" dropdown (PatientProfile.vue —
 * `unscheduledTxFollowUpCreationAllowed`, `overdueRecallFollowUpCreationAllowed`,
 * `saveInitialFollowupDate`), which is NOT edited. cypress/node-tests/followup-start.test.js holds
 * this file to those computeds and to that payload, key for key.
 *
 * Only two kinds can be started here, as on the classic page: Unscheduled Treatment (4) and
 * Overdue Recall (3). Each is refused when one is already open, and:
 *   4 — when the patient has no unscheduled treatment;
 *   3 — when a recall is already scheduled (RecallScheduled is not null).
 *
 * BelongsToListID: the list's TaskTypeID (a number) is compared NUMERICALLY with the type (the classic
 * radio's value is the string "4"). The classic page compared them with === and so never found the list;
 * fixed there the same way on 2026-10-08 (Nilay), and the test pins the two together.
 */

export const FOLLOWUP_START_TYPES = [
  { value: 4, label: 'Unscheduled Tx' },
  { value: 3, label: 'Recall' },
];

function openOfType(followups, typeID) {
  return (Array.isArray(followups) ? followups : []).filter(t => t && Number(t.TaskTypeID) === typeID);
}

/** Classic `unscheduledTxFollowUpCreationAllowed`. */
export function canStartUnscheduledTx({ followups, unscheduledTx } = {}) {
  if (openOfType(followups, 4).length > 0) return false;
  if (Array.isArray(unscheduledTx) && unscheduledTx.length === 0) return false;
  return true;
}

/** Classic `overdueRecallFollowUpCreationAllowed`. */
export function canStartRecall({ followups, patient } = {}) {
  if (openOfType(followups, 3).length > 0) return false;
  if (patient && patient.RecallScheduled !== null && patient.RecallScheduled !== undefined) return false;
  return true;
}

/** Which types can be started: `{ 4: bool, 3: bool, any: bool }`. */
export function followupStartOptions(ctx) {
  const tx = canStartUnscheduledTx(ctx);
  const recall = canStartRecall(ctx);
  return { 4: tx, 3: recall, any: tx || recall };
}

/** 'YYYY-MM-DD' + n days, as pure calendar arithmetic (no browser timezone leaks in). '' for a bad date. */
export function addDaysToYmd(ymd, n) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return '';
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n)).toISOString().slice(0, 10);
}

/**
 * WHY a type cannot be started: `{ 4: string, 3: string }`, '' when it can. The panel's blocked cards
 * (Patient Action Panels, 2b) say this instead of quietly greying a radio out. It asks the SAME
 * questions, in the same order, as canStartUnscheduledTx / canStartRecall above — so `reason === ''`
 * exactly when the type can be started (patient-profile-adapter.test.js pins that agreement).
 */
export function followupStartBlockedReasons({ followups, unscheduledTx, patient } = {}) {
  const OPEN = 'Already has an open follow-up';
  const reasons = { 4: '', 3: '' };
  if (openOfType(followups, 4).length > 0) reasons[4] = OPEN;
  else if (Array.isArray(unscheduledTx) && unscheduledTx.length === 0) reasons[4] = 'No unscheduled treatment';
  if (openOfType(followups, 3).length > 0) reasons[3] = OPEN;
  else if (patient && patient.RecallScheduled !== null && patient.RecallScheduled !== undefined) reasons[3] = 'A recall is already scheduled';
  return reasons;
}

/** The list a new follow-up of this type joins, or null. Numeric compare (see the header). */
export function belongsToListID(lists, typeID) {
  const match = (Array.isArray(lists) ? lists : []).find(l => l && Number(l.TaskTypeID) === Number(typeID));
  return match ? match.ListID : null;
}

/**
 * The payload `tasks/updateInitialFollowupDate` takes — classic `saveInitialFollowupDate`.
 * Returns `{ error }` instead when the input cannot be saved (the classic toasts, word for word).
 *
 * @param {object} p
 * @param {number|string} p.typeID      4 or 3
 * @param {string} p.followupDate       YYYY-MM-DD (native date input)
 * @param {string} p.today              YYYY-MM-DD (CreatedAt / InitialFollowupCreatedOn)
 * @param {string} [p.note]
 * @param {string} p.siteId
 * @param {number|string} p.patientNum
 * @param {string} p.teamioPatientID    '<siteID>-<PatNum>'
 * @param {string|number} p.userID      the signed-in employee (AssignedToEmpID)
 * @param {object} p.user               { ODUserNum, DefaultDB }
 * @param {Array} [p.lists]             belongsToLists rows
 */
export function buildFollowupStartPayload(p = {}) {
  if (!p.followupDate) return { error: 'Please select a date first before saving' };
  const typeID = Number(p.typeID);
  if (typeID !== 4 && typeID !== 3) {
    return { error: 'Please select a followup type of Unscheduled tx or Recall first before saving' };
  }
  const user = p.user || {};
  const payload = {
    CreatedAt: p.today,
    SiteID: p.siteId,
    CreatedAutomatically: 1,
    AssignedToEmpID: p.userID,
    DueDate: p.followupDate,
    StatusID: 1,
    TaskTypeID: String(typeID),
    PatNum: p.patientNum,
    UserODNum: user.ODUserNum,
    DefaultDB: user.DefaultDB,
    TeamioPatientID: p.teamioPatientID,
    FollowupDate: p.followupDate,
    Active: 1,
    Attempts: 0,
    BelongsToListID: Array.isArray(p.lists) && p.lists.length ? belongsToListID(p.lists, typeID) : null,
    InitialFollowupDateSet: 1,
    InitialFollowupCreatedOn: p.today,
  };
  if (p.note) payload.initialFollowupNote = p.note;
  return { payload };
}
